import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";

const AuthContext = createContext(null);
const DEFAULT_ADMIN_ENGAGEMENT = "XYZ";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(localStorage.getItem("aikyam_tenant") || "");
  const [access, setAccess] = useState(null);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data.user);
      setTenants(data.tenants);
      const stored = localStorage.getItem("aikyam_tenant");
      const valid = data.tenants.find((t) => t.id === stored);
      // Aikyam admins land on the engagement being presented unless they picked another
      const preferred = data.user?.is_platform_admin
        ? data.tenants.find((t) => t.name === DEFAULT_ADMIN_ENGAGEMENT)
        : null;
      const next = valid ? stored : (preferred?.id || data.tenants[0]?.id || "");
      setTenantId(next);
      if (next) localStorage.setItem("aikyam_tenant", next);
    } catch {
      setUser(false);
      setTenants([]);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!tenantId || !user) return setAccess(null);
    api
      .get(`/tenants/${tenantId}/access`)
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ tenant: null, actions: [], frameworks: [], roles: [], confidentiality_max: 0 }));
  }, [tenantId, user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("aikyam_token", data.access_token);
      localStorage.removeItem("aikyam_tenant");
      await loadSession();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: errMsg(e) };
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("aikyam_token");
    localStorage.removeItem("aikyam_tenant");
    // in-progress reconciliation edits are per-person and per-browser: the next person to
    // sign in on this machine must never inherit them as an overlay on published figures
    Object.keys(localStorage)
      .filter((k) => k.startsWith("fp-recon-drafts:"))
      .forEach((k) => localStorage.removeItem(k));
    setUser(false);
    setTenants([]);
    setAccess(null);
  };

  const switchTenant = (id) => {
    localStorage.setItem("aikyam_tenant", id);
    setTenantId(id);
    setAccess(null);
  };

  const value = useMemo(
    () => ({
      user,
      tenants,
      tenantId,
      tenant: tenants.find((t) => t.id === tenantId) || null,
      access,
      can: (action) => !!access?.actions?.includes(action),
      hasFramework: (fw) => !!access?.frameworks?.includes(fw),
      isPlatformAdmin: !!user?.is_platform_admin,
      login,
      logout,
      switchTenant,
      reload: loadSession,
    }),
    [user, tenants, tenantId, access] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
